import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from '../../utils/cn';
import { getTodayIST } from '../../utils/dateUtils';

interface DayTab {
  dateStr: string;        // "YYYY-MM-DD"
  weekdayLabel: string;   // "MON"
  dayLabel: string;       // "21"
  monthLabel: string;     // "Apr"
  isToday: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName?: string;
  isTradingDay: boolean;  // !isWeekend && !isHoliday
}

interface DayTabsProps {
  selectedDate: string;
  onChange: (date: string) => void;
  /** Calendar days back from today (default 7). */
  daysBack?: number;
  /** Calendar days forward from today (default 14). */
  daysForward?: number;
  /** Optional DTE value per date string. `null` → out of fetched range. */
  dteByDate?: Map<string, number | null>;
  /** Map of YYYY-MM-DD → display name for trading holidays. Defaults to {} when undefined. */
  holidays?: Record<string, string>;
  /** Notified once with the full visible date list so the parent can fetch DTE. */
  onDatesChange?: (dates: string[]) => void;
}

const WEEKDAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function isWeekend(d: Date): boolean {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}

function buildTabs(
  todayStr: string,
  back: number,
  forward: number,
  holidays: Record<string, string>,
): DayTab[] {
  const today = parseIsoDate(todayStr);
  const tabs: DayTab[] = [];
  for (let i = -back; i <= forward; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dateStr = toIsoDate(d);
    const weekend = isWeekend(d);
    const holidayName = holidays[dateStr];
    const holiday = holidayName !== undefined;
    tabs.push({
      dateStr,
      weekdayLabel: WEEKDAY_ABBR[d.getDay()],
      dayLabel: String(d.getDate()),
      monthLabel: MONTH_ABBR[d.getMonth()],
      isToday: dateStr === todayStr,
      isWeekend: weekend,
      isHoliday: holiday,
      holidayName,
      isTradingDay: !weekend && !holiday,
    });
  }
  return tabs;
}

const DayTabs: React.FC<DayTabsProps> = ({
  selectedDate,
  onChange,
  daysBack = 7,
  daysForward = 14,
  dteByDate,
  holidays,
  onDatesChange,
}) => {
  const todayStr = getTodayIST();
  // Stabilise the fallback so the `tabs` useMemo below doesn't re-run on
  // every render when `holidays` is undefined (the literal `{}` was
  // re-allocated each render and invalidated the dep array).
  const holidayMap = useMemo(() => holidays ?? {}, [holidays]);
  const tabs = useMemo(
    () => buildTabs(todayStr, daysBack, daysForward, holidayMap),
    [todayStr, daysBack, daysForward, holidayMap],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Notify parent of the visible date range so it can fetch / compute DTE.
  useEffect(() => {
    if (!onDatesChange) return;
    onDatesChange(tabs.map(t => t.dateStr));
  }, [tabs, onDatesChange]);

  // Scroll the active tab into view when selection changes.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selectedDate]);

  // Keyboard navigation: ← / → walks trading days, T jumps to today.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 't' || e.key === 'T') {
        onChange(todayStr);
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const step = e.key === 'ArrowRight' ? 1 : -1;
      const idx = tabs.findIndex((t) => t.dateStr === selectedDate);
      if (idx < 0) return;
      // Walk until we land on a (visible) trading day.
      let next = idx + step;
      while (next >= 0 && next < tabs.length && !tabs[next].isTradingDay) {
        next += step;
      }
      if (next >= 0 && next < tabs.length) {
        onChange(tabs[next].dateStr);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tabs, selectedDate, onChange, todayStr]);

  return (
    <div
      ref={containerRef}
      className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
      role="tablist"
      aria-label="Execution schedule day tabs"
    >
      {tabs.map((tab) => {
        const isActive = tab.dateStr === selectedDate;
        const dte = dteByDate?.get(tab.dateStr);
        const isExpiry = dte === 0 && tab.isTradingDay;
        // Suppress DTE on weekends/holidays — those cells aren't tradeable so
        // a DTE count there would be misleading.
        const dteLabel =
          !tab.isTradingDay || dte === undefined || dte === null
            ? null
            : dte === 0
            ? '0DTE'
            : `${dte}DTE`;
        const title = tab.isHoliday
          ? `${tab.holidayName || 'Trading holiday'} — market closed`
          : tab.isWeekend
            ? 'Weekend — market closed'
            : isExpiry
            ? 'Expiry day'
            : undefined;
        return (
          <button
            key={tab.dateStr}
            ref={isActive ? activeRef : undefined}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={!tab.isTradingDay}
            onClick={() => tab.isTradingDay && onChange(tab.dateStr)}
            title={title}
            className={cn(
              'shrink-0 px-3 py-2 rounded-lg border text-center transition-colors tabular-nums',
              'flex flex-col items-center min-w-[60px]',
              isActive && isExpiry
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-amber-400 dark:ring-amber-500 ring-offset-1 ring-offset-white dark:ring-offset-gray-900'
                : isActive
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : tab.isTradingDay
                ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                : tab.isHoliday
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/50 cursor-not-allowed'
                : 'bg-gray-50 dark:bg-gray-900 text-gray-300 dark:text-gray-600 border-gray-100 dark:border-gray-800 cursor-not-allowed',
              tab.isToday && !isActive && 'ring-1 ring-blue-400 dark:ring-blue-500',
              isExpiry && !isActive && 'ring-2 ring-amber-400 dark:ring-amber-500',
            )}
          >
            <span className="text-[10px] uppercase tracking-wider font-medium opacity-80">
              {tab.weekdayLabel}
            </span>
            <span className="text-sm font-bold leading-tight">{tab.dayLabel}</span>
            <span className="text-[10px] opacity-70">{tab.monthLabel}</span>
            {dteByDate && (
              <span
                aria-hidden={!dteLabel}
                className={cn(
                  'text-[10px] font-semibold mt-0.5 leading-none uppercase tracking-wider',
                  !dteLabel
                    ? 'invisible'
                    : isActive
                    ? 'text-white/90'
                    : isExpiry
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-blue-600 dark:text-blue-400',
                )}
              >
                {dteLabel ?? '0DTE'}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default DayTabs;